/* eslint-disable */
/**
 * @license
 * Copyright 2021 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */

/// <reference types="urlpattern-polyfill" />

import { ssrGlobalThis } from '@bcflit-v1-shared/ssr-mode/ssr-global-this';
import {isServer, type ReactiveController, type ReactiveControllerHost} from 'lit';

export interface BaseRouteConfig {
  name?: string | undefined;
  render?: (params: {[key: string]: string | undefined}) => unknown;
  enter?: (params: {
    [key: string]: string | undefined;
  }) => Promise<boolean> | boolean;
  canActivate?: (params: {[key: string]: string | undefined}) => Promise<boolean> | boolean;
  data?: Record<string, any>
  dataGenerator?: (params: {[key: string]: string | undefined}) => Record<string, any>
}

type UrlMatcher = (segments: string[]) => boolean;

/**
 * A RouteConfig that matches against a `path` string. `path` must be a
 * [`URLPattern` compatible pathname pattern](https://developer.mozilla.org/en-US/docs/Web/API/URLPattern/pathname).
 */
export interface PathRouteConfig extends BaseRouteConfig {
  path: string;
  matcher?: UrlMatcher
}

/**
 * A RouteConfig that matches against a given [`URLPattern`](https://developer.mozilla.org/en-US/docs/Web/API/URLPattern)
 *
 * While `URLPattern` can match against protocols, hostnames, and ports,
 * routes will only be checked for matches if they're part of the current
 * origin. This means that the pattern is limited to checking `pathname` and
 * `search`.
 */
export interface URLPatternRouteConfig extends BaseRouteConfig {
  pattern: URLPattern;
  matcher?: UrlMatcher
}

/**
 * A RouteConfig that matches using only a custom matcher function.
 */
export interface MatcherOnlyRouteConfig extends BaseRouteConfig {
  matcher: UrlMatcher;
}

/**
 * A description of a route, which path or pattern to match against, and a
 * render() callback used to render a match to the outlet.
 */
export type RouteConfig = PathRouteConfig | URLPatternRouteConfig | MatcherOnlyRouteConfig;

// A cache of URLPatterns created for PathRouteConfig.
// Rather than converting all given RoutConfigs to URLPatternRouteConfig, this
// lets us make `routes` mutable so users can add new PathRouteConfigs
// dynamically.
const patternCache = new WeakMap<PathRouteConfig, URLPattern>();

const isPatternConfig = (route: RouteConfig): route is URLPatternRouteConfig =>
  (route as URLPatternRouteConfig).pattern !== undefined;

const hasMatcher = (route: RouteConfig): boolean =>
  'matcher' in route && typeof (route as any).matcher === 'function';

const getPattern = (route: PathRouteConfig | URLPatternRouteConfig) => {
  if (isPatternConfig(route)) {
    return route.pattern;
  }
  let pattern = patternCache.get(route as PathRouteConfig);
  if (pattern === undefined) {
    patternCache.set(route as PathRouteConfig, (pattern = new URLPattern({pathname: (route as PathRouteConfig).path})));
  }
  return pattern;
};

const testRouteMatch = (route: RouteConfig, pathname: string): boolean => {
  // If route has a matcher function, use it instead of path/pattern
  if (hasMatcher(route)) {
    const segments = pathname.split('/').filter(segment => segment !== '');
    return (route as any).matcher(segments);
  }

  // Otherwise use standard pattern matching (only for PathRouteConfig and URLPatternRouteConfig)
  return getPattern(route as PathRouteConfig | URLPatternRouteConfig).test({pathname});
};

const getRouteParams = (route: RouteConfig, pathname: string): {[key: string]: string | undefined} => {
  // For routes with matcher function, return empty params object
  // The matcher doesn't provide parameter extraction like URLPattern
  if (hasMatcher(route)) {
    return {};
  }

  // For standard pattern/path routes, use URLPattern to extract params
  const pattern = getPattern(route as PathRouteConfig | URLPatternRouteConfig);
  const result = pattern.exec({pathname});
  return result?.pathname.groups ?? {};
};

/**
 * A reactive controller that performs location-based routing using a
 * configuration of URL patterns and associated render callbacks.
 */
export class BcfLitRoutes implements ReactiveController {
  private readonly _host: ReactiveControllerHost & HTMLElement;
  private _isConnected: boolean = false;
  /*
   * The currently installed set of routes in precedence order.
   *
   * This array is mutable. To dynamically add a new route you can write:
   *
   * ```ts
   * this._routes.routes.push({
   *   path: '/foo',
   *   render: () => html`<p>Foo</p>`,
   * });
   * ```
   *
   * Mutating this property does not trigger any route transitions. If the
   * changes may result is a different route matching for the current path, you
   * must instigate a route update with `goto()`.
   */
  routes: Array<RouteConfig> = [];

  /**
   * A default fallback route which will always be matched if none of the
   * {@link routes} match. Implicitly matches to the path "/*".
   */
  fallback?: BaseRouteConfig;

  /*
   * The current set of child Routes controllers. These are connected via
   * the routes-connected event.
   */
  public readonly _childRoutes: Array<BcfLitRoutes> = [];

  private _parentRoutes: BcfLitRoutes | undefined;

  /*
   * State related to the current matching route.
   *
   * We keep this so that consuming code can access current parameters, and so
   * that we can propagate tail matches to child routes if they are added after
   * navigation / matching.
   */
  private _currentPathname: string | undefined;
  public _currentRoute: RouteConfig | undefined;
  public _currentParams: {
    [key: string]: string | undefined;
  } = {};

  /**
   * Callback to call when this controller is disconnected.
   *
   * It's critical to call this immediately in hostDisconnected so that this
   * controller instance doesn't receive a tail match meant for another route.
   */
  // TODO (justinfagnani): Do we need this now that we have a direct reference
  // to the parent? We can call `this._parentRoutes.disconnect(this)`.
  private _onDisconnect: (() => void) | undefined;

  constructor(
    host: ReactiveControllerHost & HTMLElement,
    routes: Array<RouteConfig>,
    options?: {fallback?: BaseRouteConfig}
  ) {
    (this._host = host).addController(this);
    this.routes = [...routes];
    this.fallback = options?.fallback;
  }

  /**
   * Returns a URL string of the current route, including parent routes,
   * optionally replacing the local path with `pathname`.
   */
  link(pathname?: string): string {
    if (pathname?.startsWith('/')) {
      return pathname;
    }
    if (pathname?.startsWith('.')) {
      throw new Error('Not implemented');
    }
    pathname ??= this._currentPathname;
    return (this._parentRoutes?.link() ?? '') + pathname;
  }

  /**
   * Navigates this routes controller to `pathname`.
   *
   * This does not navigate parent routes, so it isn't (yet) a general page
   * navigation API. It does navigate child routes if pathname matches a
   * pattern with a tail wildcard pattern (`/*`).
   */
  async goto(pathname: string) {
    // this is ours to work with baseHref
    pathname = this._fixPathnameByBaseHref(pathname);
    // TODO (justinfagnani): handle absolute vs relative paths separately.
    // TODO (justinfagnani): do we need to detect when goto() is called while
    // a previous goto() call is still pending?

    // TODO (justinfagnani): generalize this to handle query params and
    // fragments. It currently only handles path names because it's easier to
    // completely disregard the origin for now. The click handler only does
    // an in-page navigation if the origin matches anyway.
    let tailGroup: string | undefined;

    if (this.routes.length === 0 && this.fallback === undefined) {
      // If a routes controller has none of its own routes it acts like it has
      // one route of `/*` so that it passes the whole pathname as a tail
      // match.
      tailGroup = pathname;
      this._currentPathname = '';
      // Simulate a tail group with the whole pathname
      this._currentParams = {0: tailGroup};
    } else {
      const route = this._getRoute(pathname);
      if (route === undefined) {
        // this is changed versus original version
        // we need this setTimeout to materialize correct value on _isConnected
        setTimeout(() => {
          if (this._isConnected){
            const el: HTMLElement | EventTarget = isServer ? ssrGlobalThis() : window;
            el.dispatchEvent(new Event('routerNotFound'));
          }
        })
        return;
      }
      const params = getRouteParams(route, pathname);
      tailGroup = getTailGroup(params);

      if (typeof route.canActivate === 'function') {
        const canActivateResult = await route.canActivate(params);
        if (canActivateResult === false) {
          return;
        }
      }

      if (typeof route.enter === 'function') {
        const success = await route.enter(params);
        // If enter() returns false, cancel this navigation
        if (success === false) {
          return;
        }
      }
      // Only update route state if the enter handler completes successfully
      this._currentRoute = route;
      this._currentParams = params;
      this._currentPathname =
        tailGroup === undefined
          ? pathname
          : pathname.substring(0, pathname.length - tailGroup.length);
    }

    // Propagate the tail match to children
    if (tailGroup !== undefined) {
      for (const childRoutes of this._childRoutes) {
        childRoutes.goto(tailGroup);
      }
    }
    this._host.requestUpdate();
    const el: HTMLElement | EventTarget = isServer ? ssrGlobalThis() : window;
    el.dispatchEvent(new Event('routerUpdated'));
  }

  /**
   * The result of calling the current route's render() callback.
   */
  outlet() {
    return this._currentRoute?.render?.(this._currentParams);
  }

  /**
   * The current parsed route parameters.
   */
  get params() {
    return this._currentParams;
  }

  /**
   * Matches `url` against the installed routes and returns the first match.
   */
  private _getRoute(pathname: string): RouteConfig | undefined {
    const matchedRoute = this.routes.find((r) =>
      testRouteMatch(r, pathname)
    );
    if (matchedRoute || this.fallback === undefined) {
      return matchedRoute;
    }
    if (this.fallback) {
      // The fallback route behaves like it has a "/*" path. This is hidden from
      // the public API but is added here to return a valid RouteConfig.
      return {...this.fallback, path: '/*'};
    }
    return undefined;
  }

  private _fixPathnameByBaseHref(pathnameToFix: string): string {
    const baseHref = this._baseHref;;
    if (baseHref === '/'){
      return pathnameToFix
    }
    let pathname: string = pathnameToFix.replace(baseHref, '');
    if (pathnameToFix.startsWith('/') && !pathname.startsWith('/')){
      pathname = `/${pathname}`
    }
    if (pathnameToFix.endsWith('/') && !pathname.endsWith('/')){
      pathname = `${pathname}/`
    }
    return pathname
  }

  private get _baseHref(): string {
    if (isServer){
      return '/'
    }
    return new URL(document.getElementsByTagName('base')[0].href).pathname
  }

  hostConnected() {
    this._isConnected = true;
    const host: HTMLElement | EventTarget = isServer ? ssrGlobalThis() : this._host;

    host.addEventListener(
      RoutesConnectedEvent.eventName,
      this._onRoutesConnected as any
    );
    const event = new RoutesConnectedEvent(this);
    host.dispatchEvent(event);
    this._onDisconnect = event.onDisconnect;
  }

  hostDisconnected() {
    this._isConnected = false;
    // When this child routes controller is disconnected because a parent
    // outlet rendered a different template, disconnecting will ensure that
    // this controller doesn't receive a tail match meant for another route.
    this._onDisconnect?.();
    this._parentRoutes = undefined;
    const host: HTMLElement | EventTarget = isServer ? ssrGlobalThis() : this._host;
    host.removeEventListener(
      RoutesConnectedEvent.eventName,
      this._onRoutesConnected as any
    );
  }

  private _onRoutesConnected = (e: RoutesConnectedEvent) => {
    // Don't handle the event fired by this routes controller, which we get
    // because we do this.dispatchEvent(...)
    if (e.routes === this) {
      return;
    }
    const childRoutes = e.routes;
    this._childRoutes.push(childRoutes);
    childRoutes._parentRoutes = this;

    e.stopImmediatePropagation();
    e.onDisconnect = () => {
      // Remove route from this._childRoutes:
      // `>>> 0` converts -1 to 2**32-1
      this._childRoutes?.splice(
        this._childRoutes.indexOf(childRoutes) >>> 0,
        1
      );
    };

    const tailGroup = getTailGroup(this._currentParams);
    if (tailGroup !== undefined) {
      childRoutes.goto(tailGroup);
    }
  };
}

/**
 * Returns the tail of a pathname groups object. This is the match from a
 * wildcard at the end of a pathname pattern, like `/foo/*`
 */
const getTailGroup = (groups: {[key: string]: string | undefined}) => {
  let tailKey: string | undefined;
  for (const key of Object.keys(groups)) {
    if (/\d+/.test(key) && (tailKey === undefined || key > tailKey!)) {
      tailKey = key;
    }
  }
  return tailKey && groups[tailKey];
};

/**
 * This event is fired from Routes controllers when their host is connected to
 * announce the child route and potentially connect to a parent routes controller.
 */
export class RoutesConnectedEvent extends Event {
  static readonly eventName = 'lit-routes-connected';
  readonly routes: BcfLitRoutes;
  onDisconnect?: () => void;

  constructor(routes: BcfLitRoutes) {
    super(RoutesConnectedEvent.eventName, {
      bubbles: true,
      composed: true,
      cancelable: false,
    });
    this.routes = routes;
  }
}

